/**
 * Timezone Migration Utilities
 * 
 * This file contains utilities to help migrate from inconsistent timezone
 * property names to a standardized 'timeZone' property name.
 */
import { normalizeTimezone } from './index';

/**
 * Interface for objects that might have timezone properties
 */
export interface WithTimezoneProperties {
  [key: string]: any;
  timezone?: string;
  timeZone?: string;
}

/**
 * Standardizes timezone property names in an object
 * Ensures both timezone and timeZone properties exist with the same value,
 * with timeZone being the standard property name.
 * 
 * @param obj The object to standardize
 * @returns The object with standardized timezone properties
 */
export const standardizeTimezoneProperties = <T extends WithTimezoneProperties>(
  obj: T
): T => {
  if (!obj) return obj;

  // Make a shallow copy to avoid modifying the original
  const result = { ...obj };
  
  // Determine which timezone value to use, with fallback to UTC
  const effectiveTimezone = normalizeTimezone(
    result.timeZone || result.timezone
  );
  
  // Set both properties for backward compatibility during migration
  result.timeZone = effectiveTimezone;
  result.timezone = effectiveTimezone;
  
  return result;
};

/**
 * Prepares meeting data for storage, ensuring proper timezone handling
 * This is a replacement for the old prepareMeetingDataForStorage function
 * 
 * @param meetingData The meeting data to prepare
 * @param creatorTimezone The timezone of the meeting creator
 * @returns The prepared meeting data with proper timezone handling
 */
export const prepareMeetingDataForStorage = <T extends WithTimezoneProperties>(
  meetingData: T,
  creatorTimezone: string
): T => {
  if (!meetingData) return meetingData;

  // Clone the data to avoid modifying the original
  const preparedData = { ...meetingData };

  // Standardize timezone properties
  const standardizedData = standardizeTimezoneProperties(preparedData);

  // Ensure the timezone is set
  const effectiveTimezone = normalizeTimezone(
    standardizedData.timeZone || creatorTimezone
  );

  standardizedData.timeZone = effectiveTimezone;
  standardizedData.timezone = effectiveTimezone;

  return standardizedData;
};

/**
 * Prepares data retrieved from storage for display, ensuring proper timezone handling
 * 
 * @param data The data retrieved from storage
 * @param userTimezone The timezone of the current user
 * @returns The prepared data with standardized timezone properties
 */
export const prepareDataFromStorage = <T extends WithTimezoneProperties>(
  data: T,
  userTimezone: string
): T => {
  if (!data) return data;

  // Clone the data to avoid modifying the original
  const preparedData = { ...data };
  
  // Standardize timezone properties
  return standardizeTimezoneProperties(preparedData);
}; 